import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, Inject, OnInit } from "@angular/core";
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogModule, MatDialogTitle, MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatRadioModule } from "@angular/material/radio";
import { TranslatePipe } from "@ngx-translate/core";
import { OptionalPageSelection } from "../../../../models/dtos";
import { IOptionalPageSelectionFormGroup } from "../../../../models/properties/IOptionalPageSelectionFormGroup";
import { CloseOnEscapeDirective } from "../../../../directives/close-on-escape.directive";

@Component({
    selector: 'app-sol-properties-proposal-optional-page-selection',
    templateUrl: './sol-properties-proposal-optional-page-selection.component.html',
    styleUrls: ['./sol-properties-proposal-optional-page-selection.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatRadioModule,
        MatCheckboxModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        MatButtonModule,
        MatDialogModule,
        TranslatePipe,
        CloseOnEscapeDirective
    ],
    standalone: true
})
export class SolPropertiesProposalOptionalPageSelectionComponent implements OnInit {
    optionalPageSelectionForm!: FormGroup<IOptionalPageSelectionFormGroup>;
    optionalPageSelection: OptionalPageSelection;
    private destroyRef = inject(DestroyRef);
    private readonly userDefinedControlNames: (keyof IOptionalPageSelectionFormGroup)[] = [
        'coverPage',
        'clarifications',
        'terms',
        'scopeOfwork',
        'proposedSolution',
        'signaturePage',
        'inclusions',
        'paymentTerms',
        'exclusions',
        'references'
    ];

    constructor(
        private readonly fb: NonNullableFormBuilder,
        @Inject(MAT_DIALOG_DATA) private readonly dialogData: OptionalPageSelection,
        private readonly dialogRef: MatDialogRef<SolPropertiesProposalOptionalPageSelectionComponent>
    ) {}

    ngOnInit(): void {
        this.initializeForm();
    }

    initializeForm(): void {
        this.optionalPageSelection = this.dialogData;
        this.optionalPageSelectionForm = this.fb.group<IOptionalPageSelectionFormGroup>({
            pageSelectionOption: this.fb.control<boolean>(this.optionalPageSelection.isUseTemplatePageDefault),
            includeFinancingOptions: this.fb.control<boolean>(this.optionalPageSelection.financialOptions),
            coverPage: this.fb.control<boolean>(this.optionalPageSelection.coverPage),
            clarifications: this.fb.control<boolean>(this.optionalPageSelection.clarifications),
            terms: this.fb.control<boolean>(this.optionalPageSelection.termsConditions),
            scopeOfwork: this.fb.control<boolean>(this.optionalPageSelection.scopeOfWork),
            proposedSolution: this.fb.control<boolean>(this.optionalPageSelection.proposedSolutions),
            signaturePage: this.fb.control<boolean>(this.optionalPageSelection.signaturePage),
            inclusions: this.fb.control<boolean>(this.optionalPageSelection.inclusions),
            paymentTerms: this.fb.control<boolean>(this.optionalPageSelection.paymentTerms),
            exclusions: this.fb.control<boolean>(this.optionalPageSelection.exclusions),
            references: this.fb.control<boolean>(this.optionalPageSelection.pageReferences)
        });

        this.updateUserDefinedControls();
    }

    pageSelectionOptionControl(): boolean {
        return this.optionalPageSelectionForm.controls.pageSelectionOption?.value ?? true;
    }

    updateUserDefinedControls(): void {
        const isUserTemplateDefaultsSelected = this.pageSelectionOptionControl();
        this.userDefinedControlNames.forEach((controlName) => {
            const control = this.optionalPageSelectionForm.controls[controlName];
            if (isUserTemplateDefaultsSelected) {
                control.disable({ emitEvent: false });
                return;
            }

            control.enable({ emitEvent: false });
        });
    }

    onOkClick() {
        this.updateSelections();
        this.dialogRef.close(this.optionalPageSelectionForm.dirty);
    }

    private updateSelections(): void {
        const formValues = this.optionalPageSelectionForm.getRawValue();

        this.optionalPageSelection.isUseTemplatePageDefault = formValues.pageSelectionOption;
        this.optionalPageSelection.financialOptions = formValues.includeFinancingOptions;
        this.optionalPageSelection.coverPage = formValues.coverPage;
        this.optionalPageSelection.clarifications = formValues.clarifications;
        this.optionalPageSelection.termsConditions = formValues.terms;
        this.optionalPageSelection.scopeOfWork = formValues.scopeOfwork;
        this.optionalPageSelection.proposedSolutions = formValues.proposedSolution;
        this.optionalPageSelection.signaturePage = formValues.signaturePage;
        this.optionalPageSelection.inclusions = formValues.inclusions;
        this.optionalPageSelection.paymentTerms = formValues.paymentTerms;
        this.optionalPageSelection.exclusions = formValues.exclusions;
        this.optionalPageSelection.pageReferences = formValues.references;
    }

}